import React from 'react';
import { C } from '../constants';
import { fluxaiServiceUrl, ssoEstado } from '../services/fluxai';
import { servicioPorSku } from '../services/catalog';
import ServiceIcon from './ServiceIcon';

// ─────────────────────────────────────────────────────────────────────────────
// FLUXAI EMBED — apertura de los servicios de FluxAI DESDE Solar Hub con la MISMA
// sesión de la suite Alebas. El embebido es un iframe al servicio (sin su shell) y
// exige DOS cosas a la vez: que FluxAI permita el framing (su CSP `frame-ancestors`
// ya incluye solar-hub.co) **y** que el token de la suite reanude sesión allá.
//
// 🔴 Hoy el token de Solar Hub va SIN FIRMA (`mintToken` → alg:none), y el
// `ingestSsoToken` de FluxAI solo acepta un token sin firma para los correos de SU
// allowlist. Para cualquier otro usuario no hay sesión → su AuthGuard redirige a
// /login → **el iframe CARGA (dispara onLoad) y muestra el login de FluxAI**, así que
// el respaldo por timeout ni se entera. Por eso el estado del puente se DECLARA de
// entrada en vez de dejar al usuario mirando un login sin explicación. El cierre real
// no es un parche acá: o el `api/` de FluxAI emite el token firmado, o el estudio se
// ensambla nativo en Solar Hub (dirección del dueño, PLAN.md §5.0).
// ─────────────────────────────────────────────────────────────────────────────

export default function FluxaiEmbed({ sku, go }) {
  const svc = servicioPorSku(sku);
  const url = fluxaiServiceUrl(sku);                       // app completa (para "Abrir en pestaña")
  const embedUrl = fluxaiServiceUrl(sku, { embed: true }); // SOLO el servicio (sin shell) + tema + SSO
  const nombre = svc?.nombre || 'Servicio FluxAI';
  const { puente } = ssoEstado();             // 'firmado' (reanuda) · 'demo' (puede pedir login) · 'sin_sesion'
  const [mode, setMode] = React.useState('iframe'); // por defecto se muestra el servicio embebido
  const [loaded, setLoaded] = React.useState(false);
  const [blocked, setBlocked] = React.useState(false);
  const loadedRef = React.useRef(false);

  // Si el iframe no dispara onLoad en ~6 s, asumimos bloqueo de framing → cae al panel.
  React.useEffect(() => {
    if (mode !== 'iframe') return undefined;
    loadedRef.current = false;
    setLoaded(false); setBlocked(false);
    const t = setTimeout(() => { if (!loadedRef.current) setBlocked(true); }, 6000);
    return () => clearTimeout(t);
  }, [mode]);

  const abrirPestana = () => { try { window.open(url, '_blank', 'noopener,noreferrer'); } catch { /* popup bloqueado */ } };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 50px)' }}>
      {/* Barra del servicio */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 18px', borderBottom: `1px solid ${C.border}`, background: C.card, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <button onClick={() => go && go('home')} style={{ padding: '6px 11px', borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>← Solar Hub</button>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.heading, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nombre}</div>
            <div style={{ fontSize: 10, color: C.teal, fontWeight: 600 }}>
              Telemetría · potenciado por FluxAI
              {puente === 'firmado' ? ' · misma sesión' : ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {mode === 'iframe' && (
            <button onClick={() => setMode('panel')} style={{ fontSize: 11, fontWeight: 700, color: C.muted, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 7, padding: '6px 11px', cursor: 'pointer' }}>‹ Volver</button>
          )}
          <button onClick={abrirPestana} style={{ fontSize: 11, fontWeight: 700, color: C.teal, background: 'transparent', border: `1px solid ${C.teal}55`, borderRadius: 7, padding: '6px 11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Abrir en pestaña ↗</button>
        </div>
      </div>

      {/* Lienzo */}
      <div style={{ position: 'relative', flex: 1, minHeight: 0, background: C.dark }}>
        {/* PANEL DE LANZAMIENTO (default, siempre fiable) */}
        {(mode === 'panel' || blocked) && (
          <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', padding: 24, overflowY: 'auto' }}>
            <div style={{ maxWidth: 480, width: '100%', textAlign: 'center', background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: '28px 26px' }}>
              <div style={{ width: 56, height: 56, margin: '0 auto 14px', borderRadius: 14, display: 'grid', placeItems: 'center', color: C.teal, background: `${C.teal}1a`, border: `1px solid ${C.teal}55` }}><ServiceIcon sku={sku} size={26} /></div>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.heading, marginBottom: 6 }}>{nombre}</div>
              {svc?.desc && <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>{svc.desc}</div>}
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 18, background: C.dark, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px' }}>
                {blocked
                  ? <>No cargó aquí en 6 s. Ábrelo en una pestaña con tu sesión activa.</>
                  : <>Este servicio se entrega desde la consola de FluxAI{puente === 'firmado' ? <> con <strong style={{ color: C.teal }}>tu misma sesión</strong> de la suite</> : ''}.</>}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={abrirPestana} style={{ background: C.teal, color: C.heading, fontWeight: 800, fontSize: 13, borderRadius: 9, padding: '11px 22px', border: 'none', cursor: 'pointer' }}>Abrir {nombre} ↗</button>
                {!blocked && (
                  <button onClick={() => setMode('iframe')} style={{ background: 'transparent', color: C.muted, fontWeight: 700, fontSize: 12.5, borderRadius: 9, padding: '11px 18px', border: `1px solid ${C.border}`, cursor: 'pointer' }}>Intentar verlo aquí</button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* EMBEBIDO (opt-in) */}
        {mode === 'iframe' && !blocked && (
          <>
            {!loaded && (
              <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: C.muted, fontSize: 12, pointerEvents: 'none' }}>Cargando {nombre}…</div>
            )}
            <iframe
              title={nombre}
              src={embedUrl}
              onLoad={() => { loadedRef.current = true; setLoaded(true); }}
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
          </>
        )}
      </div>
    </div>
  );
}
